import { useCallback, useMemo, useState } from 'react';
import { Box, Text } from '@chakra-ui/react';
import PaginationComponent from '../../Pagination';
import NoDataComponent from '../../NoData';
import TableComponent from '../../Table';
import InputSearchComponent from '../../Common/Form/input-search';
import { formatDate } from '../../../utils/helper.util';
import ImageComponent from '../../Common/Image';
import { useNavigate } from 'react-router-dom';
import SeedIcon from '../../../assets/seed-icon.svg';
import LearnMorePanel from '../../../routes/ArticleViewer/components/LearnMorePanel';
import NoResultImg from '../../../assets/no-result.svg';

const MyInvestorsDetailTableComponent = (props) => {
  const { dataTable, pagination, setPagination, totalPage, isFetching } = props;

  const navigate = useNavigate();

  const [openLearnMorePanel, setOpenLearnMorePanel] = useState(false);

  const handleChangePage = (page) => {
    setPagination((pre) => {
      return {
        ...pre,
        page
      };
    });
  };

  const handleChangeSearch = (search) => {
    setPagination((pre) => {
      return {
        ...pre,
        page: 1,
        search: search
      };
    });
  };

  const convertArticle = useCallback((row) => {
    return (
      <Box
        display={'flex'}
        alignItems={'center'}
        width={{ base: '100vw', md: 'unset' }}
        cursor={'pointer'}
        onClick={() => navigate(`/articles/${row?.article?.alternativeId || row?.article?.id}`)}
      >
        <Box position={'relative'} flexShrink={0}>
          {row?.article?.cover ? (
            <ImageComponent
              rounded={'12px'}
              w={'40px'}
              h={'40px'}
              src={row?.article?.cover}
              alt="cover"
            />
          ) : (
            <Box
              rounded={'11.38px'}
              w={'40px'}
              h={'40px'}
              bgColor={'#F2F3F7'}
              display={'flex'}
              justifyContent={'center'}
              alignItems={'center'}
              textAlign={'center'}
              fontSize={'10px'}
              lineHeight={'100%'}
            >
              No Image
            </Box>
          )}
        </Box>
        <Box ml={'12px'} display={'flex'} flexDir={'column'}>
          <Text
            fontWeight={800}
            fontSize={'xs'}
            overflow={'hidden'}
            textOverflow={'ellipsis'}
            wordBreak={'break-word'}
            sx={{
              display: '-webkit-inline-box',
              WebkitLineClamp: '1',
              WebkitBoxOrient: 'vertical'
            }}
            _hover={{
              textDecoration: 'underline'
            }}
          >
            {row?.article?.title || 'Untitled'}
          </Text>
          <Text
            fontWeight={600}
            fontSize={10}
            overflow={'hidden'}
            textOverflow={'ellipsis'}
            wordBreak={'break-word'}
            sx={{
              display: '-webkit-inline-box',
              WebkitLineClamp: '1',
              WebkitBoxOrient: 'vertical'
            }}
          >
            {row?.article?.description}
          </Text>
        </Box>
      </Box>
    );
  }, []);

  const convertCategories = useCallback((row) => {
    return (
      <Box display={'flex'} flexWrap={'wrap'} gap={'8px'}>
        {row?.article?.categories.length > 0 ? (
          row?.article?.categories?.slice(0, 3)?.map((item) => (
            <Box
              key={item.id}
              px={'8px'}
              py={'4px'}
              rounded={'80px'}
              bgColor={'#F0EDEC'}
              w={'fit-content'}
              whiteSpace={'nowrap'}
            >
              <Text title={item?.name} color="#6A4E42" fontWeight={800} fontSize={'xs'}>
                {item?.name}
              </Text>
            </Box>
          ))
        ) : (
          <Text fontSize={'xs'}>No Categories</Text>
        )}
      </Box>
    );
  }, []);

  const convertInvestorAverageShare = useCallback((row) => {
    return (
      <Text color="#204A22" fontWeight={800} fontSize={'xs'}>
        {row.currentShare}% <span className="font-[400]"> of ad revenue pool </span>
      </Text>
    );
  }, []);

  const convertPointInvested = useCallback((row) => {
    return (
      <Box display={'flex'} alignItems={'center'} gap={1}>
        <img src={SeedIcon} className="w-5 h-5" alt="" />
        <Text fontSize={'xs'} fontWeight={800}>
          {row.pointInvested || 0}
        </Text>
      </Box>
    );
  }, []);

  const convertStatus = useCallback((row) => {
    return (
      <Box
        py={2}
        px={3}
        bgColor={row.status === 'Active' ? 'growth-green' : 'error-red'}
        rounded={'80px'}
        w={'fit-content'}
      >
        <Text fontSize={'xs'} color={'white'} fontWeight={800}>
          {row.status}
        </Text>
      </Box>
    );
  }, []);

  const handleSort = (sortName) => {
    let objSort = {
      sortBy: pagination.sortBy,
      sort: pagination.sort
    };
    if (sortName !== objSort.sortBy) objSort = { sortBy: sortName, sort: 'asc' };
    else if (sortName === objSort.sortBy && objSort.sort === 'asc')
      objSort = {
        ...objSort,
        sort: 'desc'
      };
    else if (sortName === objSort.sortBy && objSort.sort === 'desc')
      objSort = {
        ...objSort,
        sort: 'asc'
      };
    setPagination({ ...pagination, ...objSort });
  };

  const columns = useMemo(
    () => [
      {
        Header: 'Article',
        accessor: (row) => convertArticle(row),
        id: 'article',
        widthCustom: '10rem',
        minWidthCustom: '10rem',
        isSortField: true
      },
      {
        Header: 'Categories',
        accessor: (row) => convertCategories(row),
        id: 'myArticles',
        widthCustom: '5rem',
        minWidthCustom: '5rem'
      },
      {
        Header: 'Investor’s average share',
        accessor: (row) => convertInvestorAverageShare(row),
        id: 'currentShare',
        widthCustom: '7rem',
        minWidthCustom: '7rem',
        isSortField: true
      },
      {
        Header: 'Total Seeds invested',
        accessor: (row) => convertPointInvested(row),
        id: 'pointInvested',
        widthCustom: '5rem',
        minWidthCustom: '5rem',
        isSortField: true
      },
      {
        Header: 'Date Invested',
        accessor: (row) => <Text fontSize={'xs'}>{formatDate(row?.createdAt, true)}</Text>,
        id: 'createdAt',
        widthCustom: '5rem',
        minWidthCustom: '5rem',
        isSortField: true
      }
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [convertInvestorAverageShare, handleSort]
  );

  return (
    <Box
      w={'full'}
      border={'1px solid'}
      borderColor={'anti-flash-lite'}
      borderRadius={{ base: 0, md: '12px' }}
      shadow={'outline'}
    >
      <Box
        bg={'white'}
        p={{ base: '16px', md: '16px 24px' }}
        display={'flex'}
        alignItems={'start'}
        minH={'72px'}
        borderTopRadius={'12px'}
        justifyContent={'space-between'}
        overflow={'auto'}
      >
        <Box
          display={'flex'}
          gap="12px"
          flexDirection={{ base: 'column', md: 'row' }}
          width={'full'}
          overflow={'auto'}
        >
          <InputSearchComponent
            setPaginationSearch={handleChangeSearch}
            placeholder={'Search by Article name'}
            setLoading={() => {}}
            width={{ base: '100%', md: '320px' }}
          />
        </Box>
      </Box>
      <Box
        bg={'white'}
        rounded={'0 0 0.75rem 0.75rem'}
        display={'flex'}
        minH={'370px'}
        flexFlow={'column'}
        overflowY={'hidden'}
        overflowX={'auto'}
        css={{
          '&::-webkit-scrollbar': {
            display: 'block',
            height: '8px' // For horizontal scrollbar
          },
          '&::-webkit-scrollbar-thumb': {
            background: '#e6e7eb',
            borderRadius: '30px'
          },
          '&::-webkit-scrollbar-track': {
            background: '#f5f5f5'
          }
        }}
      >
        <TableComponent
          isLoading={isFetching}
          columns={columns}
          data={dataTable ?? []}
          headerHeight={'46px'}
          fontsizeTable={'sm'}
          handleSort={handleSort}
          currentSort={{ sortBy: pagination.sortBy, sort: pagination.sort }}
          haveFooter={false}
        />
        {!pagination.search && dataTable?.length === 0 && !isFetching ? (
          <Box minH={'370px'} display={'flex'} alignItems={'center'} justifyContent={'center'}>
            <NoDataComponent
              title="It looks like you haven't any investors yet."
              description="Try to improve your content to find more creators!"
              titleButton="Discover Articles"
              onClick={() => navigate('/')}
              isFetching={isFetching}
            />
          </Box>
        ) : null}
        {pagination.search && dataTable?.length === 0 && !isFetching ? (
          <Box minH={'370px'} display={'flex'} alignItems={'center'} justifyContent={'center'}>
            <NoDataComponent
              icon={NoResultImg}
              title="No results found"
              description="Try a different search term"
              isFetching={isFetching}
            />
          </Box>
        ) : null}
      </Box>

      {totalPage > 0 ? (
        <Box
          borderBottomRadius={'12px'}
          p={'1.25rem 1.5rem'}
          mt={'auto'}
          borderTop="1px solid"
          borderColor={'anti-flash-lite'}
          background={'light.100'}
        >
          <PaginationComponent
            currentPage={pagination.page}
            totalPages={totalPage}
            onChangePage={handleChangePage}
          />
        </Box>
      ) : null}
      {openLearnMorePanel && <LearnMorePanel isOpen onClose={() => setOpenLearnMorePanel(false)} />}
    </Box>
  );
};

export default MyInvestorsDetailTableComponent;
